// Landing pages, one per launch-video angle (docs/brand.md): /l/fast, /l/points, /l/both.
// The phone remembers which page it landed on first, so the admin can compare how each converts.
import { app, logo, esc, icon, target } from '../dom.ts';
import { brand, titled } from '../../brand.ts';
import { go } from '../router.ts';
import { feeArm, trackStep } from '../track.ts';
import { startManual, startScan } from './new-split.ts';
import { fee as pricing, landingFeeLine, myFeeGroup } from '../fee.ts';

export type Variant = 'fast' | 'points' | 'both';
export const VARIANTS: Variant[] = ['fast', 'points', 'both'];

const POINT_VALUE = 0.02; // The Points Guy values Amex and Chase points at about 2¢ (docs/brand.md).

const steps = (fee: boolean) => `
  <div class="steps"><ol>
    <li><b>1</b><div><div style="font-weight:600">Scan the receipt <span class="sub amt">0:20</span></div><div class="sub">Every line, the tax, the fees and the tip.</div></div></li>
    <li><b>2</b><div><div style="font-weight:600">Friends scan your code <span class="sub amt">0:45</span></div><div class="sub">They tap what they had. No app, no account.</div></div></li>
    <li><b>3</b><div><div style="font-weight:600">Everyone pays you <span class="sub amt">2:00</span></div><div class="sub">Venmo or Cash App opens with their exact amount${fee ? ', plus $1 for you' : ''}.</div></div></li>
  </ol></div>`;

const calculator = (bill: number, x: number) => {
  const pts = Math.round(bill * x);
  return `
  <div class="card calc">
    <div class="lbl">Your bill, in points</div>
    <div class="calc-row">
      <label class="calc-bill">$<input id="calcBill" class="amt" inputmode="decimal" value="${bill}" aria-label="Bill total"></label>
      <div class="chips">${[1, 2, 3, 4].map((m) => `<button class="chip" data-x="${m}" aria-pressed="${m === x}">${m}x</button>`).join('')}</div>
    </div>
    <div class="calc-out"><span class="amt">${pts.toLocaleString()}</span> points <span class="sub">≈ $${Math.round(pts * POINT_VALUE).toLocaleString()} of travel</span></div>
    <p class="sub" style="margin:0;font-size:12px">Example. Points depend on your card; value at about 2¢ a point (The Points Guy). We don't issue or recommend cards.</p>
  </div>`;
};

const COPY: Record<Variant, { eyebrow: string; title: string; lede: string; stat: string; statSource: string }> = {
  fast: {
    eyebrow: 'The check is here',
    title: "The math isn't your problem.",
    lede: 'Friends scan, tap what they had and pay you back on Venmo or Cash App. No app for them. No spreadsheet for you.',
    stat: '43% of people feel awkward when the bill comes. 20% have paid the whole thing just to avoid it.',
    statSource: 'comparethemarket.com survey of 2,000 diners',
  },
  points: {
    eyebrow: 'Be the one who pays',
    title: 'Get the points. Not the problem.',
    lede: `Put the whole bill on your card and keep every point. ${brand.name} gets every dollar back, exactly what each friend had, without you chasing anyone.`,
    stat: 'US households spend about $3,945 a year eating out. On a 4x dining card that’s about 15,800 points.',
    statSource: 'BLS Consumer Expenditure Survey 2024; example math',
  },
  both: {
    eyebrow: 'Restaurant bills, fixed',
    title: 'Be the one who pays. Get paid back. Keep the points.',
    lede: 'The fastest way to split a restaurant bill, and the best reason to be the one who covers it.',
    stat: 'A $892 bill on a 4x dining card is about 3,568 points, roughly $71 of travel.',
    statSource: 'Example at about 2¢ a point (The Points Guy)',
  },
};

export function landingView(variant: Variant): void {
  if (!VARIANTS.includes(variant)) return go('/', true);
  const c = COPY[variant];
  document.title = titled(c.title);
  // The $1 test: on the points and both pages, half the phones see "$1 from every friend" (and the
  // switch starts on); the other half never see it (and it starts off). The fast page keeps the old line.
  // The $1 only happens where the split keeps the thanks on: with the fee test on, that's group B
  // (A and C turn it off at tables of 3+), so phones in A or C never see the $1 promise (QA).
  // Pricing off: nobody pays anything extra, so every phone gets the "off" side (no $1 promise).
  const thanksPossible = (myFeeGroup() ?? 'B') === 'B';
  const arm = pricing.pricing ? feeArm() : 'off';
  const fee = arm === 'on' && thanksPossible;
  const feeCard = fee ? `<div class="card fee"><div class="h" style="font-size:20px;line-height:1.25">Plus $1 from every friend, for the hassle.</div><div class="sub">It shows on their screen as its own line. You keep all of it.</div></div>` : '';
  const feeLine = arm === 'off' ? '' : arm === 'on' ? '<li>Every friend adds $1 for your hassle. You can turn it off.</li>' : '<li>Optional $1 thanks from each friend, for covering.</li>';
  let bill = 892, x = 4;
  const draw = () => {
    app.innerHTML = `
      <div class="bar">${logo}<span>No app for friends</span></div>
      <div class="body landing">
        <div class="stack" style="padding-top:12px">
          <span class="lbl">${esc(c.eyebrow)}</span>
          <h1 class="h h-xl">${esc(c.title)}</h1>
          <p class="lede">${esc(c.lede)}</p>
        </div>
        ${feeCard}
        ${variant === 'points' ? calculator(bill, x) + steps(fee) : steps(fee) + (variant === 'both' ? calculator(bill, x) : '')}
        <div class="card stat"><div class="h" style="font-size:20px;line-height:1.25">${esc(c.stat)}</div><div class="sub" style="font-size:12px;margin-top:6px">${esc(c.statSource)}</div></div>
        <ul class="trust">
          <li>${brand.name} never holds the money. Friends pay you directly.</li>
          <li>Everyone pays exactly what they had, plus their share of tax and tip.</li>
          ${landingFeeLine() ? `<li>${esc(landingFeeLine())}</li>` : feeLine}
        </ul>
      </div>
      <div class="foot">
        <label class="btn file-btn">${icon.camera} Scan a receipt<input type="file" accept="image/*" capture="environment" data-file aria-label="Scan a receipt"></label>
        <button class="btn2" data-act="manual">Type it in instead</button>
      </div>`;
  };
  draw();
  const cta = () => trackStep('landing_cta', arm && pricing.pricing ? { page: variant, fee: arm } : { page: variant });
  app.onchange = (e) => {
    const input = e.target;
    if (input instanceof HTMLInputElement && input.matches('[data-file]') && input.files?.[0]) { cta(); void startScan(input.files[0]); }
  };
  app.oninput = (e) => {
    if (!(e.target instanceof HTMLInputElement) || e.target.id !== 'calcBill') return;
    bill = Math.max(0, Math.min(100_000, Number(e.target.value.replace(/[^0-9.]/g, '')) || 0));
    const out = app.querySelector('.calc-out');
    if (out) { const pts = Math.round(bill * x); out.innerHTML = `<span class="amt">${pts.toLocaleString()}</span> points <span class="sub">≈ $${Math.round(pts * POINT_VALUE).toLocaleString()} of travel</span>`; }
  };
  app.onclick = (e) => {
    const m = target<HTMLButtonElement>(e, '[data-x]');
    if (m) { x = Number(m.dataset.x); draw(); return; }
    if (target(e, '[data-act=manual]')) { cta(); startManual(); }
  };
}
